import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo, breadcrumbLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CatalogFilterToolbar, CatalogFilterSidebar, useCatalogFilterParams } from "@/components/CatalogFilters";
import { useAuth } from "@/context/AuthContext";
import { categoryLabel, brandUrl, categoryUrl, productUrl } from "@/lib/productUrl";
import { Loader2 } from "lucide-react";

const PAGE = 24;

/**
 * Standalone brand hub — /brand/:brandSlug
 * Lists all products for a designer across every category, with filter chips.
 */
export default function BrandSilo() {
  const { brandSlug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [favs, setFavs] = useState(new Set());
  const [filters] = useCatalogFilterParams();
  const [filtered, setFiltered] = useState([]);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState(null);

  // Brand metadata (hero, description, per-category counts) — one-time fetch per slug
  useEffect(() => {
    setData(null);
    setFacets(null);
    api.brandHub(brandSlug, { skip: 0, limit: PAGE })
      .then(({ data }) => setData(data))
      .catch(() => setData({ notFound: true }));
    if (user) api.favorites().then(({ data }) => setFavs(new Set(data.map((p) => p.id)))).catch(() => {});
  }, [brandSlug, user]);

  // Fetch scoped facets once the brand designer name is known.
  useEffect(() => {
    if (!data?.designer) return;
    api.productFacets({ designer: data.designer }).then(({ data }) => setFacets(data)).catch(() => setFacets(null));
  }, [data?.designer]);

  // Filter-driven product grid — refetches whenever any URL filter changes
  useEffect(() => {
    if (!data || data.notFound || !data.designer) return;
    setLoading(true);
    const params = {
      designer: data.designer,
      color: filters.color.length ? filters.color.join(",") : undefined,
      silhouette: filters.silhouette.length ? filters.silhouette.join(",") : undefined,
      neckline: filters.neckline.length ? filters.neckline.join(",") : undefined,
      length: filters.length.length ? filters.length.join(",") : undefined,
      sleeve: filters.sleeve.length ? filters.sleeve.join(",") : undefined,
      size: filters.size.length ? filters.size.join(",") : undefined,
      price_min: filters.price_min,
      price_max: filters.price_max,
      sort: filters.sort,
      paginate: true,
      limit: 72,
    };
    api.products(params).then(({ data: d }) => {
      const list = Array.isArray(d) ? d : (d.items || d.products || []);
      setFiltered(Array.isArray(list) ? list : []);
      setFilteredTotal(d.total ?? list.length);
    }).finally(() => setLoading(false));
  }, [data?.designer, JSON.stringify(filters)]);

  if (!data) return <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;
  if (data.notFound) return <div className="max-w-[900px] mx-auto px-5 pt-24 pb-32 text-center"><h1 className="font-display text-5xl">Designer not found</h1><p className="font-body mt-4 text-muted-foreground">This designer isn't in our collection yet.</p><Link to="/shop" className="inline-block mt-6 font-label text-[10px] text-rosemetal underline">← Back to Shop</Link></div>;

  const path = brandUrl(brandSlug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Designers", path: "/shop" },
    { name: data.designer, path },
  ];

  const catCounts = data.category_counts || {};
  const catEntries = Object.entries(catCounts).sort((a, b) => b[1] - a[1]);

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    numberOfItems: data.total,
    itemListElement: filtered.slice(0, 20).map((p, i) => ({
      "@type": "ListItem", position: i + 1, url: `${ORIGIN}${productUrl(p)}`, name: p.name,
    })),
  };
  const brandLd = {
    "@context": "https://schema.org",
    "@type": "Brand",
    name: data.designer,
    url: `${ORIGIN}${path}`,
  };

  return (
    <div data-testid={`brand-${brandSlug}`} className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 pb-24">
      <Seo
        title={`${data.designer} Prom & Formal Dresses`}
        description={`Shop ${data.total} ${data.designer} formalwear looks — dress locks, AI try-on, Wisconsin fitting.`}
        path={path}
        jsonLd={[breadcrumbLd(crumbs), itemListLd, brandLd]}
      />
      <Breadcrumbs items={crumbs} />


      <header className="relative overflow-hidden py-14 mb-8">
        {data.hero_url && (
          <div className="absolute inset-0 -z-10">
            <img src={data.hero_url} alt={data.designer} className="w-full h-full object-cover object-center opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-b from-ivory/40 via-ivory/70 to-ivory" />
          </div>
        )}
        <div className="text-center relative">
          <p className="font-label text-[10px] text-rosemetal tracking-widest mb-3">DESIGNER</p>
          {data.logo_url ? (
            <img src={data.logo_url} alt={data.designer} data-testid="brand-logo" className="mx-auto h-20 sm:h-24 w-auto max-w-[440px] object-contain" />
          ) : (
            <h1 className="font-display text-6xl sm:text-7xl leading-none">{data.designer}</h1>
          )}
          {data.tagline && <p className="font-body italic text-lg text-ink/80 mt-4 max-w-xl mx-auto">"{data.tagline}"</p>}
          <p className="font-body text-sm text-muted-foreground mt-4">
            {data.total} designer looks
          </p>
          {catEntries.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-6 flex-wrap">
              {catEntries.map(([cslug, n]) => (
                <Link key={cslug} to={categoryUrl(cslug)} className="font-label text-[10px] tracking-widest text-rosemetal hover:text-ink transition-colors border border-border rounded-sm px-3 py-1.5 bg-ivory/80 backdrop-blur">
                  {categoryLabel(cslug)} · {n}
                </Link>
              ))}
            </div>
          )}
          {data.description && (
            <p className="font-body text-sm text-ink/80 mt-6 max-w-2xl mx-auto leading-relaxed">{data.description}</p>
          )}
        </div>
      </header>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8 mt-4">
        <CatalogFilterSidebar hideDesigner={true} availableFacets={facets} />
        <div>
          <CatalogFilterToolbar hideDesigner={true} activeCount={filteredTotal} availableFacets={facets} />
          {loading && filtered.length === 0 ? (
            <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-rosemetal" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display text-3xl mb-2">Nothing matches those filters.</p>
              <p className="font-body text-sm text-muted-foreground">Try clearing a couple to see more looks.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((p, i) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  index={i}
                  isFav={favs.has(p.id)}
                  focusColor={filters.color.length === 1 ? filters.color[0] : null}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
